import React, { useContext, useEffect, useRef, useState } from 'react'
import { renderToStaticMarkup } from 'react-dom/server'
import Heading from '@components/Heading'
import Divider from '@components/Divider'
import { BUTTON_TYPE, DIVIDER_SIZE, HEADING_TYPE } from '@helpers/constants'
import Button from '@components/Button'
import { nanoid } from 'nanoid'
import { connectToEditorjs } from '@blocks/connectToEditorjs'
import Icon from '@components/Icon'
import { MAX_CATEGORIES, MAX_IMAGES_PER_CATEGORY } from '../constants'
import Form from '@components/Form'
import EditExerciseWrapper from '@components/EditExerciseWrapper'
import { uploadImage } from '@helpers/api'
import { storagePath } from '@helpers/variables'
import { AppContext } from '@state/context'
import { addOnSaveCallback } from '@state/actions/editorActions'

const TITLE = 'Dra bilder till kategori'

/**
 * DragImageToCategory
 * @returns
 */
const DragImageToCategoryComponent = (props) => {
  const { initialState, handleStateChange, id, onSave } = props
  const [state, setState] = useState(initialState)
  const { name, preamble = {}, image = null, categories = [] } = state
  const [_, dispatch] = useContext(AppContext)

  useEffect(() => {
    dispatch(
      addOnSaveCallback({
        id,
        callback: async () => {
          let newImage = await (async () => {
            if (!image?.previewUrl) return image
            try {
              const filename = await uploadImage(image.previewUrl)

              return { filename: `${storagePath}/${filename}` }
            } catch (error) {
              console.log(error)
            }
          })()

          const newCategories = await Promise.all(
            categories?.map(async (category) => {
              const images = await Promise.all(
                category?.images.map(async (image) => {
                  if (!image?.previewUrl) return image
                  try {
                    const filename = await uploadImage(image.previewUrl)

                    return {
                      ...image,
                      previewUrl: null,
                      filename: `${storagePath}/${filename}`,
                    }
                  } catch (error) {
                    console.log(error)
                  }
                })
              )
              return {
                ...category,
                images,
              }
            })
          )

          handleStateChange({
            ...state,
            image: newImage,
            categories: newCategories,
          })
        },
      })
    )
  }, [state])

  /**
   * Broadcast state change up
   */
  useEffect(() => {
    handleStateChange(state)
  }, [state])

  /**
   * Remove category
   * @param {*} pair
   */
  const removeCategory = (pair) => {
    setState({
      ...state,
      categories: categories.filter((q, k) => k !== pair),
    })
  }

  /**
   * Add new question
   */
  const addNewCategory = () => {
    setState({
      ...state,
      categories: [
        ...categories,
        {
          name: '',
          id: nanoid(),
          images: Array(MAX_IMAGES_PER_CATEGORY)
            .fill(null)
            .map(() => ({ previewUrl: null })),
        },
      ],
    })
  }

  const moveCategory = (fromIndex, toIndex) => {
    const element = categories[fromIndex]
    categories.splice(fromIndex, 1)
    categories.splice(toIndex, 0, element)

    setState({
      ...state,
      categories: [...categories],
    })
  }

  /**
   * Update category
   * @param {*} categoryIndex
   * @param {*} key
   * @param {*} value
   */
  const updateCategory = (categoryIndex, attribute, value) => {
    setState({
      ...state,
      categories: categories.map((category, index) => {
        if (categoryIndex === index) {
          category[attribute] = value
        }
        return category
      }),
    })
  }

  const updateImage = (categoryIndex, imageIndex, value) => {
    setState({
      ...state,
      categories: categories.map((category, index) => {
        if (categoryIndex === index) {
          category.images[imageIndex].previewUrl = value
        }
        return category
      }),
    })
  }

  const removeImage = (categoryIndex, imageIndex) => {
    setState({
      ...state,
      categories: categories.map((category, index) => {
        if (categoryIndex === index) {
          delete category.images[imageIndex].filename
          category.images[imageIndex].previewUrl = null
        }
        return category
      }),
    })
  }

  return (
    <div>
      <EditExerciseWrapper type={TITLE} onSave={onSave}>
        {/* ----- Name ----- */}
        <Heading headingLevel={HEADING_TYPE.H3}>Namn på övningen</Heading>
        <Form.Editor
          content={name}
          handleChange={(value) => setState({ ...state, name: value })}
          elementId={`category-name-${id}`}
        />
        <Divider size={DIVIDER_SIZE.SMALL} />
        <Heading headingLevel={HEADING_TYPE.H3}>Inledande text</Heading>
        <Form.Editor
          content={preamble}
          handleChange={(value) => setState({ ...state, preamble: value })}
          // placeholder={'Skriv en inledande text om övningen'}
          elementId={`category-preamble-${id}`}
        />

        <Divider size={DIVIDER_SIZE.SMALL} />

        <Heading headingLevel={HEADING_TYPE.H3}>Bild</Heading>
        <Form.ImageInput
          handleImageChange={(e) => {
            e.preventDefault()

            setState({ ...state, image: { previewUrl: e.target.files[0] } })
          }}
          id={'image'}
          imagePreviewUrl={
            image?.previewUrl !== null && typeof image?.previewUrl === 'object'
              ? URL.createObjectURL(image?.previewUrl)
              : image?.filename
          }
          inputKey={'inputKey'}
          name={'image'}
          onDelete={() => {
            setState({ ...state, image: null })
          }}
        />
        <Divider size={DIVIDER_SIZE.SMALL} />

        {/* ----- Pairs ----- */}
        <ul>
          {categories.map((category, categoryIndex) => {
            if (!category.id) {
              updateCategory(categoryIndex, 'id', nanoid())
            }

            return (
              <li key={category.id}>
                <div className={''}>
                  <div>
                    <Heading headingLevel={HEADING_TYPE.H3}>
                      Kategori {categoryIndex + 1}
                    </Heading>
                    <Divider size={DIVIDER_SIZE.LINE} line />
                    <Divider size={DIVIDER_SIZE.SMALL} />
                    <Form.Input
                      defaultValue={category.name}
                      onChange={(e) =>
                        updateCategory(categoryIndex, 'name', e.target.value)
                      }
                      placeholder='Skriv kategorin här'
                    />
                  </div>

                  <Divider size={DIVIDER_SIZE.SMALL} />

                  <p className={'font-bold'}>Bilder</p>
                  <p>Lägg till de bilder som tillhör kategorin här</p>

                  <Divider size={DIVIDER_SIZE.SMALL} />

                  <div className={'grid grid-cols-3 gap-4 w-max'}>
                    {category.images.map((image, imageIndex) => {
                      return (
                        <div className={'w-[80px]'} key={imageIndex}>
                          <Form.ImageInput
                            handleImageChange={(e) => {
                              e.preventDefault()

                              updateImage(
                                categoryIndex,
                                imageIndex,
                                e.target.files[0]
                              )
                            }}
                            id={categoryIndex + '-' + imageIndex}
                            imagePreviewUrl={
                              image?.filename ??
                              (image?.previewUrl !== null &&
                              typeof image?.previewUrl === 'object'
                                ? URL.createObjectURL(image?.previewUrl)
                                : null)
                            }
                            inputKey={categoryIndex + '-' + imageIndex}
                            name={'image'}
                            onDelete={() => {
                              removeImage(categoryIndex, imageIndex)
                            }}
                          />
                        </div>
                      )
                    })}
                  </div>
                </div>

                <Divider size={DIVIDER_SIZE.SMALL} />

                <Button
                  type={BUTTON_TYPE.PRIMARY}
                  handleClick={() => removeCategory(categoryIndex)}
                  marginRight
                >
                  Ta bort kategori
                </Button>

                <Button
                  type={BUTTON_TYPE.PRIMARY}
                  disabled={categoryIndex === 0}
                  handleClick={() =>
                    moveCategory(categoryIndex, categoryIndex - 1)
                  }
                  marginRight
                >
                  Flytta upp
                </Button>
                <Button
                  type={BUTTON_TYPE.PRIMARY}
                  disabled={categoryIndex + 1 === categories.length}
                  handleClick={() =>
                    moveCategory(categoryIndex, categoryIndex + 1)
                  }
                >
                  Flytta ner
                </Button>

                <Divider
                  size={
                    categoryIndex + 1 === categories.length
                      ? DIVIDER_SIZE.MEDIUM
                      : DIVIDER_SIZE.SMALL
                  }
                />
              </li>
            )
          })}
        </ul>

        <Button
          type={BUTTON_TYPE.PRIMARY}
          disabled={categories.length >= MAX_CATEGORIES}
          handleClick={addNewCategory}
        >
          Lägg till en kategori
        </Button>
        <Divider size={DIVIDER_SIZE.MEDIUM} />
      </EditExerciseWrapper>
    </div>
  )
}

export const DragImageToCategory = connectToEditorjs(
  DragImageToCategoryComponent,
  TITLE,
  renderToStaticMarkup(<Icon icon={'ClipboardAdd'} />)
)
